import { createContext, useContext } from 'react';
import { createRootRouteWithContext } from '@tanstack/react-router';

import { AppShell } from './AppShell';
import type { RouterContext } from './requireCapability';
import type { InventoriesResponse } from '../api/inventories';
import type { ApiContact, ContactsResponse } from '../api/contacts';
import type { ApiOrganisation, OrganisationsResponse } from '../api/organisations';
import type { CapacitiesResponse } from '@api/capacities';
import type { OperatingHour } from '../api/operatingHours';
import type { Inventory } from '../components/Inventory/types';
import type { ApiResaleItem } from '../api/resaleItems';
import type { ApiProduct } from '../api/products';
import type { ProductCategoryOptions } from '../components/Product/categories';
import type { Order, Salesperson } from '../api/orders';
import type { ApiEventSummary } from '../api/events';
import type { OrderSource } from '../api/orderSources/types';
import type { CurrentUser } from '../lib/capabilities';
import type { SlingPosition, User, UsersResponse } from '../api/users';
import type { Discount } from '../api/discounts';
import type { Surcharge } from '../api/surcharges';
import type { OauthApplicationShowResponse } from '../api/oauthApplications';
import type { TranslationObject } from '../hooks/useTranslation';

type InitialPriceModifier =
  | { kind: 'discount'; record: Discount }
  | { kind: 'surcharge'; record: Surcharge };

// Each Phlex view ships its own section's translations and seed data, so the
// router must only own that section's URLs. Cross-section URLs aren't in the
// tree; popstate lands on the notFound boundary which hands control back to
// Turbo so the destination section remounts AppRouter with its own bag.
export type Section =
  | 'contacts'
  | 'inventories'
  | 'closed_hours'
  | 'resale_items'
  | 'order_sources'
  | 'price_modifiers'
  | 'products'
  | 'events'
  | 'orders'
  | 'capacities'
  | 'preset_menus'
  | 'bundles'
  | 'locations'
  | 'organisations'
  | 'users'
  | 'scheduler'
  | 'oauth'
  | 'dashboard'
  | 'sales_tracking';

export interface AppRouterProps {
  section: Section;
  initialData?: InventoriesResponse;
  initialContacts?: ContactsResponse;
  initialContact?: ApiContact;
  initialInventory?: Inventory;
  initialClosedHour?: OperatingHour;
  initialResaleItem?: ApiResaleItem;
  initialOrderSource?: OrderSource;
  initialPriceModifier?: InitialPriceModifier;
  initialProduct?: ApiProduct;
  productCategories?: ProductCategoryOptions;
  eventSalespersons?: Salesperson[];
  initialOrder?: Order;
  initialEvent?: ApiEventSummary;
  orderSalespersons?: Salesperson[];
  orderSources?: OrderSource[];
  orderAllCount?: number;
  initialOrganisations?: OrganisationsResponse;
  initialOrganisation?: ApiOrganisation;
  initialUsers?: UsersResponse;
  initialUser?: User;
  slingPositions?: SlingPosition[];
  initialOauthPayload?: OauthApplicationShowResponse;
  currentUser?: CurrentUser;
  editOptions?: { categories: string[]; locations: string[] };
  servingDate?: string;
  initialCapacities?: CapacitiesResponse;
  papercutUrl?: string;
  // Page-shaped: each route's component reads only the keys it owns.
  translations?: TranslationObject;
}

// Default value is never read — useAppRouterInitial is only called from
// routes rendered inside AppRouter's <Provider>.
export const InitialContext = createContext<AppRouterProps>({} as AppRouterProps);
export const useAppRouterInitial = () => useContext(InitialContext);

// Router carries the signed-in user in its context so beforeLoad guards
// (requireCapability) can read it. Threaded in via buildAppRouter → createRouter.
export const rootRoute = createRootRouteWithContext<RouterContext>()({ component: AppShell });
